import { useTranslations } from '@vocab/react';
import { Actions, Box, Button, Drawer, Stack } from 'braid-design-system';
import { observer } from 'mobx-react';
import { useState, type ReactElement } from 'react';

import type { BUCKETS } from 'src/stores/JobApplicationStore/types';
import useBucketLabel from 'src/utils/hooks/useBucketLabel';
import useQueryParamsStore from 'src/utils/hooks/useQueryParamsStore';
import { useStores } from 'src/utils/hooks/useStores';

import type { FilterFns } from '../../../components/app/FilterSideBar/useFilters';
import { FilterSearchBar } from '../FilterSearchBar';
import type { ApplicationStatusFacets } from '../utils/getStatusFacetsFromQueryData';

import translations from './.vocab';
import { FilterAccordion } from './FilterAccordion';
import { StatusFolderButtonsMobile } from './StatusFolderButtonsMobile';

import * as styles from './FilterDrawerMobile.css';

export interface SelectedBucketInfo {
  type: BUCKETS;
  count: number | null;
  newCount?: number;
  selected: boolean;
}

interface FilterDrawerProps {
  showDrawer: boolean;
  setShowDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  useFiltersFns: FilterFns;
  shouldShowClearFilterButton: boolean;
  statusFilters: {
    onClickStatusFolder: (bucket: BUCKETS) => void;
    statusFacets: ApplicationStatusFacets;
  };
  screeningQuestionFilters: {
    selectedQuestions: string[];
    content: ReactElement;
  } | null;
  locationFilter: {
    selectedLocations: string[];
    content: ReactElement;
  } | null;
  nationalityFilter: {
    selectedNationalities: string[];
    content: ReactElement;
  } | null;
}

type FilterType =
  | 'search-names'
  | 'status-folders'
  | 'location'
  | 'nationality'
  | 'screening-questions';

export const FilterDrawerMobile: React.FC<FilterDrawerProps> = observer(
  ({
    showDrawer,
    setShowDrawer,
    useFiltersFns,
    statusFilters,
    locationFilter,
    nationalityFilter,
    screeningQuestionFilters,
    shouldShowClearFilterButton,
  }) => {
    const { t } = useTranslations(translations);

    const { jobApplicationStore } = useStores();
    const [, setQueryQueryParams] = useQueryParamsStore();

    const [expandedFilter, setExpandedFilter] = useState<FilterType | null>(
      null,
    );

    const { getLabelFromBucket } = useBucketLabel();

    // FIXME: this is declared here and in FilterTabletDesktopContentLayout because declaring it in AppPageBlock breaks it somehow because mobx isn't updating the value properly. Not sure why, we should lift this up when we move searchText away from mobx
    const onSearch = () => {
      jobApplicationStore.clearAllSelected();
      return setQueryQueryParams.navigateToSearch(
        jobApplicationStore.searchText,
      );
    };
    const onChange = (text: string) => {
      jobApplicationStore.updateSearchText(text);
    };

    return (
      <Drawer
        id="filter-drawer"
        width="large"
        closeLabel={t('Close')}
        title={t('Filter applications by')}
        open={showDrawer}
        onClose={() => setShowDrawer(false)}
      >
        <Stack space="xsmall">
          <FilterAccordion
            label={t('Name search')}
            expanded={expandedFilter === 'search-names'}
            onClick={() => {
              setExpandedFilter((prev) => {
                if (prev === 'search-names') {
                  return null;
                }
                return 'search-names';
              });
            }}
            selectedItems={
              jobApplicationStore.lastResultSearchText
                ? [jobApplicationStore.lastResultSearchText]
                : []
            }
          >
            <FilterSearchBar
              onChange={onChange}
              value={jobApplicationStore.searchText}
              lastResultSearchText={jobApplicationStore.lastResultSearchText}
              onSearch={onSearch}
            />
          </FilterAccordion>
          <FilterAccordion
            label={t('Status')}
            expanded={expandedFilter === 'status-folders'}
            onClick={() => {
              setExpandedFilter((prev) => {
                if (prev === 'status-folders') {
                  return null;
                }
                return 'status-folders';
              });
            }}
            selectedItems={[getLabelFromBucket(jobApplicationStore.bucket)]}
          >
            <StatusFolderButtonsMobile
              selectedStatusFolder={
                jobApplicationStore.bucket &&
                !jobApplicationStore.lastResultSearchText
                  ? jobApplicationStore.bucket
                  : null
              }
              onClickStatusFolder={statusFilters.onClickStatusFolder}
              statusFacets={statusFilters.statusFacets}
            />
          </FilterAccordion>
          {locationFilter ? (
            <FilterAccordion
              label={t('Location')}
              expanded={expandedFilter === 'location'}
              onClick={() => {
                setExpandedFilter((prev) => {
                  if (prev === 'location') {
                    return null;
                  }
                  return 'location';
                });
              }}
              selectedItems={locationFilter.selectedLocations}
            >
              {locationFilter.content}
            </FilterAccordion>
          ) : null}
          {nationalityFilter ? (
            <FilterAccordion
              label={t('Nationality')}
              expanded={expandedFilter === 'nationality'}
              onClick={() => {
                setExpandedFilter((prev) => {
                  if (prev === 'nationality') {
                    return null;
                  }
                  return 'nationality';
                });
              }}
              selectedItems={nationalityFilter.selectedNationalities}
            >
              {nationalityFilter.content}
            </FilterAccordion>
          ) : null}
          {screeningQuestionFilters ? (
            <FilterAccordion
              label={t('Screening questions')}
              expanded={expandedFilter === 'screening-questions'}
              onClick={() => {
                setExpandedFilter((prev) => {
                  if (prev === 'screening-questions') {
                    return null;
                  }
                  return 'screening-questions';
                });
              }}
              selectedItems={screeningQuestionFilters.selectedQuestions}
            >
              {screeningQuestionFilters.content}
            </FilterAccordion>
          ) : null}
        </Stack>
        <Box
          paddingY="xsmall"
          paddingX="small"
          background="surface"
          className={styles.stickyButton}
          zIndex={1}
        >
          <Actions>
            <Button
              type="button"
              tone="formAccent"
              onClick={() => setShowDrawer(false)}
            >
              {t('View applicationCount applications', {
                applicationCount:
                  jobApplicationStore.currentTotalOfJobApplications,
              })}
            </Button>
            {shouldShowClearFilterButton ? (
              <Button
                variant="soft"
                tone="neutral"
                onClick={() =>
                  useFiltersFns.resetFilters({
                    eventName: 'clear_filters_pressed',
                    actionOrigin: 'candidate listing - filter section',
                  })
                }
              >
                {t('Clear all')}
              </Button>
            ) : null}
          </Actions>
        </Box>
      </Drawer>
    );
  },
);
